import { PlatformLocation } from '@angular/common';
import { Injectable } from '@angular/core';
import { DAY_IN_MS } from '@office-start/date-utility';
import {
    createExpirationPlugin,
    readFromSimplePersistence,
    writeToSimplePersistence
} from '@office-start/simple-persistence';
import { logRequest } from '@office-start/telemetry-service';
import { KnownRequestInstrumentationEvents } from '@office-start/telemetry-types';

import { OfficeHomePageErrorHandler } from '../../error-handler/error-handler.service';
import { ErrorId } from '../../error-handler/error-id';
import { HttpStatusCode } from '../constants/http-status-code.enum';
import { OfficeRoute } from '../constants/office-route.enum';
import { InstrumentationParam, InstrumentationService, KnownInstrumentationEvents } from '../instrumentation/instrumentation.service';
import { FetchStrategy, InstrumentedHttp, InstrumentedHttpDataResponse, InstrumentedHttpPromise } from '../instrumentation/instrumented-http.service';
import { UserProfileService } from '../instrumentation/user-profile.service';
import { FileItem, FileType } from '../models/file-item.model';
import { HttpRequestOptions } from '../models/http-request-options.model';
import { GraphDriveItemCollection } from '../models/onedrive-graph.model';
import { OperationStream } from '../models/operation-stream.model';
import { AccessTokenConsumer, AccessTokenService } from '../services/access-token.service';
import { ConsumerSkuType } from '../services/consumer-license.service';
import { BrowserType, DeviceDetector, DeviceInfo, PlatformType } from '../services/device-detector.service';
import { OfficeRouteService } from '../services/office-route.service';
import { OfficeHomeApiConfigService } from '../services/officehome-api-config.service';
import { SchedulerService } from '../services/scheduler.service';
import { UpdateContentService } from '../services/update-content.service';
import { Scenario, ScenarioType, VroomService } from '../services/vroom.service';
import { XhrService, XhrServiceError } from '../services/xhr.service';
import { CapabilitiesService } from '../shared-context/capabilities.service';
import { AuthType, ClientStartupConfigService } from '../shared-context/client-startup-config.service';
import { ConsumerInstallConfigService } from '../shared-context/consumer-install-config.service';
import { ContextService } from '../shared-context/context.service';
import { LocService } from '../shared-context/loc.service';
import { UserContextService } from '../shared-context/user-context.service';
import { ObservableHelper } from '../utilities/observable-helper.utility';
import { TemplatesConfigService } from './templates-config.service';
import { EnterpriseTemplatesFolder, RawTemplatesCollectionsResponse, RawTemplatesRecommendedResponse, RawTemplatesResponse, Template, TemplateData, TemplatesCollectionData, TemplateThumbnail } from './templates.model';

// TODO: Refactor TemplatesService out of XhrService
@Injectable({
    providedIn: 'root'
})
export class TemplatesService extends XhrService {
    private templatesFromConfig: Map<FileType, Array<Template>>;
    private static readonly TEMPLATES_COLLECTION_PATH = '/collections';
    private static readonly TEMPLATES_RECOMMENDED_PATH = '/recommended';
    private static readonly TEMPLATES_CONTENT_PATH = '/content';
    private static readonly collectionsArg = 'collections=';
    private static readonly cultureArg = 'culture=';
    private static readonly appArg = 'app=';
    private static readonly appsArg = 'apps=';
    private static readonly limitArg = 'show=';
    private static readonly isEduArg = 'isEdu=';
    private static readonly flightsArg = 'flights=';
    private static readonly isIwArg = 'isIw=';
    private static readonly isAdminArg = 'isAdmin=';
    private static readonly isSmbArg = 'isSmb=';
    private static readonly isEduFacultyArg = 'isEduFaculty=';
    private static readonly isEduStudentArg = 'isEduStudent=';
    private static readonly authIdArg = 'authId=';
    private static readonly pageArg = 'page=';
    private static readonly firstVisitAgeInWeeksArg = 'firstVisitAgeInWeeks=';
    private static readonly browserNameArg = 'browserName=';
    private static readonly osNameArg = 'osName=';
    private static readonly osVersionArg = 'osVersion=';
    private static readonly deploymentEnvironmentArg = 'deploymentEnvironment=';
    private static readonly isPremiumString = '&isPremium=1';
    private static readonly templateIdArg = 'id=';
    private static readonly templateBinaryUrlArg = 'templateBinaryUrl=';
    private static readonly supportedEnterpriseFileTypes: Set<string> = new Set<string>(['pptx', 'potx', 'dotx', 'xltx']);
    private static readonly templatesCollectionPersistenceKey = 'templates-collection-api';
    private static readonly templatesCollectionPersistenceVersion = 5;
    private static readonly templatesPersistencePlugins = [createExpirationPlugin(7 * DAY_IN_MS)];
    private static readonly templatesRecommendedPersistenceKey = 'templates-recommended-api';
    private static readonly templatesRecommendedPersistenceVersion = 3;

    private readonly deviceInfo: DeviceInfo;
    private readonly locale: string;
    private readonly numTemplates: number;
    private readonly requestConfig: HttpRequestOptions;
    private readonly templatesStreamMap: Map<FileType, OperationStream<TemplateData>>;
    private readonly enterpriseTemplatesStreamMap: Map<string, OperationStream<TemplateData>>;
    private readonly templatesContentStreamMap: Map<FileType, Map<string, OperationStream<any>>>;
    private createTemplatesStream: OperationStream<TemplateData>;
    private enterpriseTemplateLibraries: Promise<Array<EnterpriseTemplatesFolder>>;

    constructor(
        accessTokenService: AccessTokenService,
        private readonly capabilitesService: CapabilitiesService,
        private readonly clientStartupConfigService: ClientStartupConfigService,
        private readonly consumerInstallConfigService: ConsumerInstallConfigService,
        contextService: ContextService,
        private readonly deviceDetector: DeviceDetector,
        private readonly globalErrorHandler: OfficeHomePageErrorHandler,
        instrumentationService: InstrumentationService,
        instrumentedHttp: InstrumentedHttp,
        officeHomeApiConfigService: OfficeHomeApiConfigService,
        private readonly officeRouteService: OfficeRouteService,
        platformLocation: PlatformLocation,
        private readonly startupConfigService: ClientStartupConfigService,
        private readonly templatesConfig: TemplatesConfigService,
        private readonly updateContentService: UpdateContentService,
        private readonly userContextService: UserContextService,
        private readonly userProfileService: UserProfileService,
        readonly locService: LocService,
        scheduler: SchedulerService,
        private readonly vroomService: VroomService
    ) {
        super(accessTokenService, instrumentationService, instrumentedHttp, AccessTokenConsumer.OfficeHome, contextService, officeHomeApiConfigService, platformLocation, scheduler);
        this.locale = contextService.sharedContext.officeMarket.toLowerCase();
        this.numTemplates = this.templatesConfig.config.numTemplates;
        this.initializeTemplateCache();
        this.requestConfig = {
            headers: new Map()
        };

        this.templatesStreamMap = new Map<FileType, OperationStream<TemplateData>>();
        this.enterpriseTemplatesStreamMap = new Map<string, OperationStream<TemplateData>>();
        this.templatesContentStreamMap = new Map<FileType, Map<string, OperationStream<any>>>();
        this.deviceInfo = this.deviceDetector.getDeviceInfo();
    }

    public getTemplateContent(type: FileType, templateId: string, templateBinaryUrl: string): OperationStream<any> {
        if (this.templatesContentStreamMap.has(type) && this.templatesContentStreamMap.get(type).has(templateBinaryUrl)) {
            return this.templatesContentStreamMap.get(type).get(templateBinaryUrl);
        }

        const url = `${this.getUrl(type, this.templatesConfig.config.templatesApiEndpoint + TemplatesService.TEMPLATES_CONTENT_PATH)}&${TemplatesService.templateIdArg}${templateId}&${TemplatesService.templateBinaryUrlArg}${templateBinaryUrl}`;

        const templateContentDataFromCache = this.fetchTemplateContentData(type, url, templateId, FetchStrategy.CacheOnly);
        const templateContentDataFromNetwork = this.fetchTemplateContentData(type, url, templateId, FetchStrategy.NetworkFirst);

        const templateContentStream = ObservableHelper.createResultsWithCacheObservable(
            templateContentDataFromCache,
            templateContentDataFromNetwork,
            null,
            _ => {
                if (this.templatesContentStreamMap.get(type)) {
                    this.templatesContentStreamMap.get(type).delete(templateBinaryUrl);
                }
            }
        );

        if (!this.templatesContentStreamMap.get(type)) {
            this.templatesContentStreamMap.set(type, new Map<string, OperationStream<any>>());
        }

        this.templatesContentStreamMap.get(type).set(templateBinaryUrl, templateContentStream);

        return templateContentStream;
    }

    public getVisioLocalizationLabel(ft: FileType) {
        return this.locService[`${FileType[ft]}TemplateLabel`];
    }

    public async getTemplatesCollections(collectionIds: Array<string>): Promise<Array<TemplatesCollectionData>> {
        if (collectionIds && collectionIds.length > 0) {
            let collectionsFromCache = [];
            let collectionIdsToLookFor = [];
            const collectionDict = {};

            try {
                collectionsFromCache = await Promise.all(
                    collectionIds.map(
                        collectionId => readFromSimplePersistence<TemplatesCollectionData>({
                            userId: this.clientStartupConfigService.config.puid,
                            collectionName: TemplatesService.templatesCollectionPersistenceKey,
                            collectionVersion: TemplatesService.templatesCollectionPersistenceVersion,
                            key: this.getCacheKey(collectionId),
                            plugins: TemplatesService.templatesPersistencePlugins
                        })));
                for (let i = 0; i < collectionIds.length; ++i) {
                    if (!!collectionsFromCache[i]) {
                        collectionDict[collectionIds[i]] = collectionsFromCache[i];
                    } else {
                        collectionIdsToLookFor.push(collectionIds[i]);
                    }
                }

                if (collectionIdsToLookFor.length === 0) {
                    // Log this request as success from indexedDB cache
                    logRequest({
                        eventName: KnownRequestInstrumentationEvents.TemplatesCollectionsRequest,
                        httpCode: HttpStatusCode.OK,
                        fromCache: true,
                        latency: -1
                    });
                    return collectionsFromCache;
                }
            } catch (e) {
                collectionIdsToLookFor = collectionIds;
            }

            try {
                const collectionsFromNetwork: Array<TemplatesCollectionData> = await this.fetchTemplatesCollections(collectionIdsToLookFor.join(','));
                collectionsFromNetwork.forEach(dataFromNetwork => {
                    if (dataFromNetwork && dataFromNetwork.Templates && dataFromNetwork.Templates.Template && dataFromNetwork.Templates.Template.length > 0) {
                        writeToSimplePersistence({
                            userId: this.clientStartupConfigService.config.puid,
                            collectionName: TemplatesService.templatesCollectionPersistenceKey,
                            collectionVersion: TemplatesService.templatesCollectionPersistenceVersion,
                            key: this.getCacheKey(dataFromNetwork.CollectionId),
                            data: dataFromNetwork,
                            plugins: TemplatesService.templatesPersistencePlugins
                        });
                    }
                });

                for (let i = 0; i < collectionIdsToLookFor.length; ++i) {
                    collectionDict[collectionIdsToLookFor[i]] = collectionsFromNetwork[i];
                }

                return collectionIds.map(collectionId => collectionDict[collectionId]).filter(x => !!x);
            } catch (e) {
                return collectionsFromCache.filter(x => !!x);
            }
        } else {
            return [];
        }
    }

    public async getTemplatesRecommended(apps: string): Promise<TemplateData> {
        if (apps && apps.length > 0) {
            try {
                const dataFromCache: TemplateData = await readFromSimplePersistence({
                    userId: this.clientStartupConfigService.config.puid,
                    collectionName: TemplatesService.templatesRecommendedPersistenceKey,
                    collectionVersion: TemplatesService.templatesRecommendedPersistenceVersion,
                    key: this.getCacheKey(apps),
                    plugins: TemplatesService.templatesPersistencePlugins
                });

                if (dataFromCache) {
                    return dataFromCache;
                }
            } catch (e) {
                // do nothing
            }

            const serviceUrl = this.getTemplatesRecommendedUrl(apps);
            try {
                const dataFromNetwork = await this.fetchTemplatesRecommended(apps, serviceUrl);
                if (dataFromNetwork && dataFromNetwork.Template && dataFromNetwork.Template.length) {
                    writeToSimplePersistence({
                        userId: this.clientStartupConfigService.config.puid,
                        collectionName: TemplatesService.templatesRecommendedPersistenceKey,
                        collectionVersion: TemplatesService.templatesRecommendedPersistenceVersion,
                        key: this.getCacheKey(apps),
                        data: dataFromNetwork,
                        plugins: TemplatesService.templatesPersistencePlugins
                    });
                }
                return dataFromNetwork;
            } catch (e) {
                // do nothing
            }
        }
        return null;
    }

    public getTemplates(type: FileType): OperationStream<TemplateData> {
        if (this.templatesStreamMap.has(type)) {
            return this.templatesStreamMap.get(type);
        }

        const url = this.getUrl(type, this.templatesConfig.config.templatesApiEndpoint);

        const templateDataFromCache = this.fetchTemplateData(type, url, FetchStrategy.CacheOnly);
        const templateDataFromNetwork = this.fetchTemplateData(type, url, FetchStrategy.NetworkFirst);
        const updateStream = this.updateContentService.getOrAddUpdateStream(this.getUpdateStreamKey(url), () => this.fetchTemplateData(type, url, FetchStrategy.NetworkFirst));

        const templateStream = ObservableHelper.createResultsWithCacheObservable(
            templateDataFromCache,
            templateDataFromNetwork,
            updateStream,
            _ => this.templatesStreamMap.delete(type)
        );

        this.templatesStreamMap.set(type, templateStream);
        return templateStream;
    }

    public updateTemplates(type: FileType): Promise<void> {
        this.templatesFromConfig.delete(type);
        this.templatesStreamMap.delete(type);
        return this.updateContentService.pushUpdate(this.getUpdateStreamKey(this.getUrl(type, this.templatesConfig.config.templatesApiEndpoint)));
    }

    public getCreateTemplates(): OperationStream<TemplateData> {
        if (this.createTemplatesStream) {
            return this.createTemplatesStream;
        }
        const currentPage = 'Other';
        const authId = this.startupConfigService.config.authType === AuthType.OrgId ? 2 : 1;
        const flights = this.startupConfigService.config.sharedClientStartupContext.flights.replace(/,\s*$/, '');
        const type = 'Create';
        const limit = '20';
        const templatesMetadataApiQueryParams = `&${TemplatesService.isIwArg}${this.userContextService.user.isIw}&${TemplatesService.isAdminArg}${this.userContextService.user.isAdmin}&${TemplatesService.isSmbArg}${this.userContextService.user.isSmb}&${TemplatesService.isEduFacultyArg}${this.userContextService.user.isEduFaculty}&${TemplatesService.isEduStudentArg}${this.userContextService.user.isEduStudent}&${TemplatesService.flightsArg}${flights}&${TemplatesService.authIdArg}${authId}&${TemplatesService.pageArg}${currentPage}&${TemplatesService.firstVisitAgeInWeeksArg}${Math.floor(this.userProfileService.fullDaysSinceFirstVisit() / 7)}&${TemplatesService.browserNameArg}${BrowserType[this.deviceInfo.browser.browserType]}&${TemplatesService.osNameArg}${PlatformType[this.deviceInfo.platform.platformType]}&${TemplatesService.osVersionArg}${this.deviceInfo.platformVersion}&${TemplatesService.deploymentEnvironmentArg}${this.startupConfigService.config.sharedClientStartupContext.deploymentEnvironment}`;
        const url = `${this.contextService.sharedContext.officeHomeApiRoot}/${this.templatesConfig.config.templatesApiEndpoint}?${TemplatesService.appArg}${type}&${TemplatesService.cultureArg}${this.locale}&${TemplatesService.limitArg}${limit}&${TemplatesService.isEduArg}${this.userContextService.user.isEdu}${templatesMetadataApiQueryParams}`;

        const templateDataFromCache = this.fetchTemplateData(FileType.None, url, FetchStrategy.CacheOnly);
        const templateDataFromNetwork = this.fetchTemplateData(FileType.None, url, FetchStrategy.NetworkFirst);
        const templateStream = ObservableHelper.createResultsWithCacheObservable(
            templateDataFromCache,
            templateDataFromNetwork,
            null,
            _ => this.createTemplatesStream = null
        );

        this.createTemplatesStream = templateStream;
        return templateStream;
    }

    public getEnterpriseTemplates(driveId: string, itemId: string = '', largeThumbnails: boolean = false): OperationStream<TemplateData> {
        const key = driveId.concat(itemId);

        if (this.enterpriseTemplatesStreamMap.has(key)) {
            return this.enterpriseTemplatesStreamMap.get(key);
        }

        const getTemplateData = (data: GraphDriveItemCollection, cache: boolean): TemplateData => {
            let templates = [];

            if (data) {
                templates = data.value
                    .filter(graphDriveItem => TemplatesService.supportedEnterpriseFileTypes.has(FileItem.getFileExtension(graphDriveItem.name)) || graphDriveItem.folder)
                    .map(graphDriveItem => {
                        const thumbnails: Array<TemplateThumbnail> = [];
                        if (graphDriveItem.thumbnails.length > 0) {
                            const thumbnail = graphDriveItem.thumbnails[0];

                            if (largeThumbnails) {
                                thumbnails.push({
                                    Size: 'large',
                                    Height: thumbnail.large.height,
                                    Width: thumbnail.large.width,
                                    Url: thumbnail.large.url,
                                    ImageType: 'MT'
                                });
                            } else {
                                thumbnails.push({
                                    Size: 'medium',
                                    Height: thumbnail.medium.height,
                                    Width: thumbnail.medium.width,
                                    Url: thumbnail.medium.url,
                                    ImageType: 'MT'
                                });
                            }
                        }

                        return {
                            AssetId: graphDriveItem.id,
                            Title: graphDriveItem.name,
                            TemplateBinaryUrl: this.capabilitesService.capabilities.pwaEnabled ? graphDriveItem.webDavUrl : graphDriveItem.webUrl,
                            Thumbnails: thumbnails
                        } as Template;
                });
            }

            return {
                Template: templates,
                fromCache: cache
            };
        };

        const enterpriseTemplateDataFromCache = this.vroomService.getDriveItemChildrenWithThumbnails(driveId, itemId, FetchStrategy.CacheOnly).then(data => getTemplateData(data, true));
        const enterpriseTemplateDataFromNetwork = this.vroomService.getDriveItemChildrenWithThumbnails(driveId, itemId, FetchStrategy.NetworkFirst, Scenario.BrowseOrgTemplates, ScenarioType.Active).then(data => getTemplateData(data, false));

        const enterpriseTemplatesStream = ObservableHelper.createResultsWithCacheObservable(
            enterpriseTemplateDataFromCache,
            enterpriseTemplateDataFromNetwork,
            null,
            _ => this.enterpriseTemplatesStreamMap.delete(key)
        );

        this.enterpriseTemplatesStreamMap.set(key, enterpriseTemplatesStream);
        return enterpriseTemplatesStream;
    }

    public getRequestIdFromOmexResponse(response: InstrumentedHttpDataResponse<any>): string {
        return this.getHeaderValue(response.headers, 'x-ms-request-id');
    }

    public getEnterpriseTemplatesLibraries(): Promise<Array<EnterpriseTemplatesFolder>> {
        if (this.enterpriseTemplateLibraries) {
            return this.enterpriseTemplateLibraries;
        }

        this.enterpriseTemplateLibraries = this.vroomService.getEnterpriseTemplatesLibraries().then(data => {
            let templateLibraries = [];

            if (data) {
                templateLibraries = data.value.map(graphDriveItem => ({
                    DriveId: graphDriveItem.id,
                    ItemId: '',
                    Title: graphDriveItem.name
                } as EnterpriseTemplatesFolder));
            }

            return templateLibraries;
        });

        return this.enterpriseTemplateLibraries;
    }

    public async isPaidConsumer(): Promise<boolean> {
        if (this.clientStartupConfigService.config.authType === AuthType.MSA) {
            const skuType = await this.consumerInstallConfigService.getCurrentSku();
            return (skuType !== null) && !(skuType === ConsumerSkuType.NonActive || skuType === ConsumerSkuType.NotSubscribed || skuType === ConsumerSkuType.NotDetected || skuType === ConsumerSkuType.Perpetual || skuType === ConsumerSkuType.Perpetual2019);
        }
        return false;
    }

    protected handleQueryParam(paramNameInLowerCase: string, value: string | Array<string>) {
        if (paramNameInLowerCase === 'mocktemplates' && this.mockRequestHeader) {
            this.requestConfig.headers.set(this.mockRequestHeader, value);
        }
    }

    private fetchTemplateContentData(type: FileType, templateContentUrl: string, templateId: string, strategy: FetchStrategy): Promise<any> {
        return new Promise<any>(async (resolve, reject) => {
            const appString = FileItem.fileTypeNameFromType(type);
            const promiseCallback = (promise: InstrumentedHttpPromise<any>) => {
                promise
                    .instrument((result, params) => {
                        if (result.ok) {
                            if (result.data) {
                                params[InstrumentationParam.ItemCategory] = appString;
                                params[InstrumentationParam.ElementId] = templateId;
                            } else {
                                params[InstrumentationParam.HttpCode] = HttpStatusCode.UnexpectedResponse.toString();
                            }
                        }
                    })
                    .then(result => {
                        if (!result.data) {
                            reject(new XhrServiceError(result, 'Null template content response'));
                            return;
                        }

                        resolve(result);
                    })
                    .catch(error => {
                        this.globalErrorHandler.handleError(error);
                    });
            };

            this.get<RawTemplatesResponse>(
                KnownInstrumentationEvents.Template_Content_Request,
                KnownRequestInstrumentationEvents.TemplateContentRequest,
                templateContentUrl,
                promiseCallback,
                reject,
                this.requestConfig,
                strategy);
        });
    }

    private fetchTemplatesCollections(collections: string): Promise<Array<TemplatesCollectionData>> {
        const serviceUrl = this.getTemplatesCollectionUrl(collections);
        return new Promise<Array<TemplatesCollectionData>>(async (resolve, reject) => {
            const promiseCallback = (promise: InstrumentedHttpPromise<RawTemplatesCollectionsResponse>) => {
                promise
                    .instrument((result, params) => {
                        if (result.ok) {
                            if (result.data && result.data.Collections && result.data.Collections.length > 0) {
                                const itemsCount = result.data.Collections.length;
                                const requestId = result.data.Collections[0].Templates?.RequestId;
                                params[InstrumentationParam.ActualSize] = itemsCount.toString();
                                params[InstrumentationParam.ItemCategory] = collections;
                                params[InstrumentationParam.RequestId] = requestId;
                            } else {
                                params[InstrumentationParam.Error] = 'Unexpected response';
                                params[InstrumentationParam.HttpCode] = HttpStatusCode.UnexpectedResponse.toString();
                            }
                        } else {
                            params[InstrumentationParam.Error] = result.statusText;
                        }
                    })
                    .then(result => {
                        if (!result.data || !result.data.Collections || result.data.Collections.length === 0) {
                            reject(new XhrServiceError(result, 'Null templates response'));
                            return;
                        }

                        const collectionsData: Array<TemplatesCollectionData> =
                            result.data.Collections.map(collection => {
                                if (collection && collection.Templates && collection.Templates.Value && collection.Templates.Value.length > 0) {
                                    return {
                                        CollectionId: collection.Collection,
                                        Title: collection.Collection,
                                        Templates: {
                                            Template: this.setupTemplates(collection.Templates.Value, FileType.None),
                                            fromCache: false
                                        }
                                    };
                                }
                                return null;
                            });

                        if (!collectionsData || collectionsData.length === 0) {
                            reject(new XhrServiceError(result, 'Null templates response'));
                        }
                        resolve(collectionsData);
                    })
                    .catch(error => {
                        this.globalErrorHandler.handleError(error, ErrorId.KAzhF);
                    });
            };

            this.get<RawTemplatesCollectionsResponse>(
                KnownInstrumentationEvents.TemplatesCollectionsRequest,
                KnownRequestInstrumentationEvents.TemplatesCollectionsRequest,
                serviceUrl,
                promiseCallback,
                reject,
                this.requestConfig,
                FetchStrategy.NetworkOnly);
        });
    }

    private fetchTemplatesRecommended(apps: string, serviceUrl: string): Promise<TemplateData> {
        return new Promise<TemplateData>(async (resolve, reject) => {
            const promiseCallback = (promise: InstrumentedHttpPromise<RawTemplatesRecommendedResponse>) => {
                promise
                    .instrument((result, params) => {
                        if (result.ok) {
                            if (result.data && result.data.Templates.Value && result.data.Templates.Value.length > 0) {
                                const itemsCount = result.data.Templates.Value.length;
                                const requestId = result.data.Templates.RequestId;
                                params[InstrumentationParam.ActualSize] = itemsCount.toString();
                                params[InstrumentationParam.ItemCategory] = apps;
                                params[InstrumentationParam.RequestId] = requestId;
                            } else {
                                params[InstrumentationParam.HttpCode] = HttpStatusCode.UnexpectedResponse.toString();
                            }
                        }
                    })
                    .then(result => {
                        if (!result.data || !result.data.Templates || result.data.Templates.Value.length === 0) {
                            reject(new XhrServiceError(result, 'Null templates response'));
                            return;
                        }

                        const templates: TemplateData = {
                            Template: this.setupTemplates(result.data.Templates.Value, FileType.None),
                            fromCache: false
                        };

                        if (!templates || templates.Template.length === 0) {
                            reject(new XhrServiceError(result, 'Null templates response'));
                        }
                        resolve(templates);
                    })
                    .catch(error => {
                        this.globalErrorHandler.handleError(error, ErrorId.KlVan);
                    });
            };

            const paidConsumer = await this.isPaidConsumer();
            if (paidConsumer) {
                serviceUrl += TemplatesService.isPremiumString;
            }

            this.get<RawTemplatesRecommendedResponse>(
                KnownInstrumentationEvents.TemplatesRecommendedRequest,
                KnownRequestInstrumentationEvents.TemplatesRecommendedRequest,
                serviceUrl,
                promiseCallback,
                reject,
                this.requestConfig,
                FetchStrategy.NetworkOnly);
        });
    }

    private fetchTemplateData(type: FileType, serviceUrl: string, strategy: FetchStrategy): Promise<TemplateData> {
        return new Promise<TemplateData>(async (resolve, reject) => {
            if (this.templatesFromConfig.has(type)) {
                resolve({
                    Template: this.templatesFromConfig.get(type),
                    fromCache: strategy === FetchStrategy.CacheOnly
                });
                return;
            }

            const appString = FileItem.fileTypeNameFromType(type);
            const promiseCallback = (promise: InstrumentedHttpPromise<RawTemplatesResponse>) => {
                promise
                    .instrument((result, params) => {
                        if (result.ok) {
                            if (result.data && result.data.Templates && result.data.Templates.Value) {
                                const itemsCount = result.data.Templates.Value.length;
                                params[InstrumentationParam.ActualSize] = itemsCount.toString();
                                params[InstrumentationParam.ItemCategory] = appString;
                                params[InstrumentationParam.RequestId] = result.data.Templates.RequestId;
                            } else {
                                params[InstrumentationParam.HttpCode] = HttpStatusCode.UnexpectedResponse.toString();
                                params[InstrumentationParam.Error] = 'Null templates response';
                            }
                        } else {
                            params[InstrumentationParam.Error] = result.statusText;
                        }
                    })
                    .then(result => {
                        if (!result.data || !result.data.Templates || !result.data.Templates.Value) {
                            reject(new XhrServiceError(result, 'Null templates response'));
                            return;
                        }

                        const templates = this.setupTemplates(result.data.Templates.Value, type);
                        resolve({
                            Template: templates,
                            fromCache: result.fromCache
                        });
                    })
                    .catch(error => {
                        this.globalErrorHandler.handleError(error, ErrorId.TFeVI);
                    });
            };

            const paidConsumer = await this.isPaidConsumer();
            if (paidConsumer) {
                serviceUrl += TemplatesService.isPremiumString;
            }

            this.get<RawTemplatesResponse>(
                KnownInstrumentationEvents.TemplatesRequest,
                KnownRequestInstrumentationEvents.TemplatesRequest,
                serviceUrl,
                promiseCallback,
                reject,
                this.requestConfig,
                strategy);
        });
    }

    private initializeTemplateCache() {
        this.templatesFromConfig = new Map<FileType, Array<Template>>();
        this.templatesConfig.getTemplatesInfoPromise().then(templatesFromConfig => {
            if (!templatesFromConfig) {
                return;
            }

            const app = FileItem.fileTypeNameToFileType(templatesFromConfig.Application);
            if (app !== FileType.Unknown) {
                const templates = this.setupTemplates(templatesFromConfig.Templates.Value, app);
                this.templatesFromConfig.set(app, templates);
            }
        });
    }

    private setupTemplates(serviceTemplates: Array<Template>, app: FileType): Array<Template> {
        if (app === FileType.Visio) {
            for (const template of serviceTemplates) {
                if (template.Categories.length) {
                    template.Title = template.Categories[0].LocalizedTitle;
                }
            }
        }
        return serviceTemplates.reduce(function (arr, template) {
            if (typeof template.AssetId !== 'undefined') {
                template.IsBlank = false;
                arr.push(template);
            }
            return arr;
        }, []);
    }

    private getUpdateStreamKey(url: string): string {
        return `templates-${url}` ;
    }

    private getUrl(type: FileType, templateEndpoint: string): string {
        let numTemplatesToShow = this.numTemplates;
        if ([FileType.VisioBusinessWac, FileType.VisioFlowChartWac, FileType.VisioGeneralWac, FileType.VisioNetworkWac, FileType.VisioSoftwareWac].lastIndexOf(type) >= 0) {
            numTemplatesToShow = this.templatesConfig.config.numVisioTemplates;
        }
        const currentRoute = this.officeRouteService.getRouteItemForCurrentLocation();
        const currentPage = currentRoute && (currentRoute.route === OfficeRoute.Start || currentRoute.route === OfficeRoute.Templates) ? `${currentRoute.route}Page` : 'Other';
        const authId = this.startupConfigService.config.authType === AuthType.OrgId ? 2 : 1;
        const flights = this.startupConfigService.config.sharedClientStartupContext.flights.replace(/,\s*$/, '');
        const templatesMetadataApiQueryParams = `&${TemplatesService.isIwArg}${this.userContextService.user.isIw}&${TemplatesService.isAdminArg}${this.userContextService.user.isAdmin}&${TemplatesService.isSmbArg}${this.userContextService.user.isSmb}&${TemplatesService.isEduFacultyArg}${this.userContextService.user.isEduFaculty}&${TemplatesService.isEduStudentArg}${this.userContextService.user.isEduStudent}&${TemplatesService.flightsArg}${flights}&${TemplatesService.authIdArg}${authId}&${TemplatesService.pageArg}${currentPage}&${TemplatesService.firstVisitAgeInWeeksArg}${Math.floor(this.userProfileService.fullDaysSinceFirstVisit() / 7)}&${TemplatesService.browserNameArg}${BrowserType[this.deviceInfo.browser.browserType]}&${TemplatesService.osNameArg}${PlatformType[this.deviceInfo.platform.platformType]}&${TemplatesService.osVersionArg}${this.deviceInfo.platformVersion}&${TemplatesService.deploymentEnvironmentArg}${this.startupConfigService.config.sharedClientStartupContext.deploymentEnvironment}`;
        return `${this.contextService.sharedContext.officeHomeApiRoot}/${templateEndpoint}?${TemplatesService.appArg}${FileType[type]}&${TemplatesService.cultureArg}${this.locale}&${TemplatesService.limitArg}${numTemplatesToShow - 1}&${TemplatesService.isEduArg}${this.userContextService.user.isEdu}${templatesMetadataApiQueryParams}`;
    }

    private getTemplatesCollectionUrl(collections: string): string {
        return `${this.contextService.sharedContext.officeHomeApiRoot}/${this.templatesConfig.config.templatesApiEndpoint + TemplatesService.TEMPLATES_COLLECTION_PATH}?${TemplatesService.collectionsArg}${collections}&${TemplatesService.cultureArg}${this.locale}`;
    }

    private getTemplatesRecommendedUrl(apps: string): string {
        const currentPage = 'Create';
        const authId = this.startupConfigService.config.authType === AuthType.OrgId ? 2 : 1;
        const flights = this.startupConfigService.config.sharedClientStartupContext.flights.replace(/,\s*$/, '');
        const limit = '200';
        const templatesMetadataApiQueryParams = `&${TemplatesService.browserNameArg}${BrowserType[this.deviceInfo.browser.browserType]}&${TemplatesService.flightsArg}${flights}&${TemplatesService.isIwArg}${this.userContextService.user.isIw}&${TemplatesService.isAdminArg}${this.userContextService.user.isAdmin}&${TemplatesService.isSmbArg}${this.userContextService.user.isSmb}&${TemplatesService.isEduFacultyArg}${this.userContextService.user.isEduFaculty}&${TemplatesService.isEduStudentArg}${this.userContextService.user.isEduStudent}&${TemplatesService.authIdArg}${authId}&${TemplatesService.firstVisitAgeInWeeksArg}${Math.floor(this.userProfileService.fullDaysSinceFirstVisit() / 7)}&${TemplatesService.pageArg}${currentPage}&${TemplatesService.deploymentEnvironmentArg}${this.startupConfigService.config.sharedClientStartupContext.deploymentEnvironment}&${TemplatesService.osNameArg}${PlatformType[this.deviceInfo.platform.platformType]}&${TemplatesService.osVersionArg}${this.deviceInfo.platformVersion}`;
        const url = `${this.contextService.sharedContext.officeHomeApiRoot}/${this.templatesConfig.config.templatesApiEndpoint + TemplatesService.TEMPLATES_RECOMMENDED_PATH }?${TemplatesService.cultureArg}${this.locale}&${TemplatesService.appsArg}${apps}&${TemplatesService.limitArg}${limit}${templatesMetadataApiQueryParams}`;
        return url;
    }

    private getCacheKey(key: string): string {
        return key + '_' + this.locale;
    }
}
